// @ts-nocheck

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { authorizedFetch, getAgentToken } from '../authTeo';
import { useChat } from '~/contexts/chat';
import * as St from './styles';
import teo from '~/assets/icons/teo.png';
import { FiHome, FiMessageSquare } from 'react-icons/fi';
import { VscSend } from 'react-icons/vsc';

export type ChatMessage = {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  createdAt: number;
};

export type Conversation = {
  id: number;
  title: string;
  lastActivityAt: number;
  sourceId: string;
  isTemp: boolean;
  status: string;
};

const ChatDrawer = () => {
  const { isChatOpen, closeChat } = useChat();
  const [activeTab, setActiveTab] = useState<'chat' | 'history'>('chat');
  const [activeId, setActiveId] = useState<string>('');
  const [inputValue, setInputValue] = useState('');

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>({});
  const [typingByConversation, setTypingByConversation] = useState<
    Record<string, boolean>
  >({});
  const [socketToken, setSocketToken] = useState<string | null>(null);

  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<any>(null);
  const justCreatedConversationRef = useRef<string | null>(null);

  const currentMessages = messages[activeId] || [];

  const getUser = () => {
    try {
      return JSON.parse(localStorage.getItem('@NovaEra:user') || '{}');
    } catch {
      return {};
    }
  };

  const user = getUser();
  const userName = user?.nomeTitular;
  const userId = user?.id;
  const userEmail = user?.email;

  useEffect(() => {
    if (isChatOpen) {
      const fetchToken = async () => {
        try {
          const token = await getAgentToken();
          if (token) setSocketToken(token);
        } catch (e) {
          console.error('Falha ao obter token para WS', e);
        }
      };
      fetchToken();
    }
  }, [isChatOpen]);

  const socketUrl = socketToken
    ? `${process.env.REACT_APP_MS_AGENT_INTERAC_WEBSOCKET}/v1/agent-interacts/chatbot`
    : null;

  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(
    socketUrl,
    {
      protocols: socketToken ? ['access_token', socketToken] : [],
      shouldReconnect: (closeEvent) => closeEvent.code !== 4003,
      onOpen: () => console.log('WebSocket Conectado'),
      onClose: (event) => {
        if (event.code > 4000) setSocketToken(null);
      },
      onError: (err) => console.error('Erro no WebSocket:', err),
      reconnectAttempts: 10,
      reconnectInterval: 3000,
    },
    !!socketToken,
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (readyState === ReadyState.OPEN) {
        sendJsonMessage({ type: 'PING' });
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [readyState, sendJsonMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (activeTab === 'chat') {
      scrollToBottom();
    }
  }, [currentMessages, typingByConversation[activeId], isChatOpen, activeTab]);

  const loadConversations = async (isBackground = false) => {
    if (!userId) return;

    try {
      const response = await authorizedFetch(
        `${process.env.REACT_APP_BANKNOW_API_URL}/v1/agent-interacts/conversations?userId=${userId}`,
      );
      if (!response.ok) throw new Error('Erro ao buscar conversas');

      const data = await response.json();

      const convsFromApi: Conversation[] = (data.conversations || []).map(
        (c: any) => ({
          id: String(c.id),
          title: c.title || 'Nova conversa',
          lastActivityAt: c.lastActivityAt
            ? c.lastActivityAt * 1000
            : Date.now(),
          sourceId: c.sourceId || crypto.randomUUID(),
          isTemp: false,
          status: c.status,
        }),
      );

      setConversations((prev) => {
        const activeIsTemp = prev.find((c) => c.id === activeId && c.isTemp);
        const tempToKeep = activeIsTemp ? [activeIsTemp] : [];
        const apiIds = new Set(convsFromApi.map((c) => c.id));
        const finalTemps = tempToKeep.filter((t) => !apiIds.has(t.id));
        return [...finalTemps, ...convsFromApi];
      });
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (isChatOpen && !activeId) {
      const initializeChat = async () => {
        setIsLoadingHistory(true);
        await loadConversations();
      };
      initializeChat();
    }
  }, [isChatOpen]);

  const loadConversationDetails = async (
    userId: string,
    conversationId: string,
  ) => {
    try {
      const res = await authorizedFetch(
        `${process.env.REACT_APP_BANKNOW_API_URL}/v1/agent-interacts/find-conversation-details?userId=${userId}&conversationId=${conversationId}`,
      );
      if (!res.ok) throw new Error('Erro ao buscar detalhes da conversa');
      const data = await res.json();
      const mapped = data.map((msg: any) => ({
        id: msg.id,
        role: msg.role,
        text: msg.text,
        createdAt: msg.createdAt * 1000,
      }));
      setMessages((prev) => ({ ...prev, [conversationId]: mapped }));
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsLoadingChat(false);
    }
  };

  const handleNewChat = () => {
    const tempId = crypto.randomUUID();
    const tempSourceId = crypto.randomUUID();

    const conv: Conversation = {
      id: tempId,
      title: 'Nova conversa',
      lastActivityAt: Date.now(),
      sourceId: tempSourceId,
      isTemp: true,
      status: 'open',
    };

    setConversations((prev) => [conv, ...prev]);
    setMessages((prev) => ({ ...prev, [tempId]: [] }));
    setActiveId(tempId);
    setActiveTab('chat');
  };

  const handleHistorySelect = (id: string) => {
    setActiveId(id);
    setActiveTab('chat');
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
    });
  };

  const formatDateHour = (ts: number) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    lastMessageRef.current = lastMessage;
  }, [lastMessage]);

  useEffect(() => {
    lastMessageRef.current = null;
  }, [activeId]);

  useEffect(() => {
    if (!lastMessage) return;

    try {
      const data = JSON.parse(lastMessage.data);
      if (!data) return;

      let effectiveConversationId = String(data.conversationId);
      const tempConversation = conversations.find(
        (c) => c.id === activeId && c.isTemp,
      );

      if (tempConversation) {
        const newId = effectiveConversationId;
        justCreatedConversationRef.current = newId;
        setActiveId(newId);
        setConversations((prev) =>
          prev.map((c) =>
            c.id === activeId
              ? { ...c, id: newId, isTemp: false, lastActivityAt: Date.now() }
              : c,
          ),
        );
        setMessages((prev) => {
          const tempMsgs = prev[activeId] || [];
          const { [activeId]: _, ...rest } = prev;
          return { ...rest, [newId]: tempMsgs };
        });
        loadConversations(true);
      }

      setMessages((prev) => {
        const updated = {
          ...prev,
          [effectiveConversationId]: [
            ...(prev[effectiveConversationId] || []),
            {
              id: crypto.randomUUID(),
              role: 'assistant',
              text: data.message,
              createdAt: Date.now(),
            },
          ],
        };
        setTypingByConversation((prev) => ({
          ...prev,
          [effectiveConversationId]: false,
        }));
        return updated;
      });
    } catch (err) {
      console.error('Error parsing WS message', err);
    }
  }, [lastMessage]);

  useEffect(() => {
    if (!activeId) return;
    const activeConv = conversations.find((c) => c.id === activeId);
    if (!activeConv || activeConv.isTemp) return;

    if (!messages[activeId] || messages[activeId].length === 0) {
      setIsLoadingChat(true);
      loadConversationDetails(userId, activeId);
    }
  }, [activeId, conversations, messages, userId]);

  const suggestions = [
    'Como saber se o cliente já possui um cartão ativo?',
    'Como reemitir um cartão perdido ou bloqueado?',
    'Como o limite é definido? Posso consultar o limite do cliente?',
  ];

  const handleSuggestionClick = (text: string) => {
    setInputValue(text);
    setViewMode('conversations');
    setActiveTab('chat');
    setTimeout(() => {
      handleSend(text);
    }, 100);
  };

  const handleHomeInputEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      setViewMode('conversations');
      setActiveTab('chat');
      setTimeout(() => {
        handleSend();
      }, 100);
    }
  };

  const [viewMode, setViewMode] = useState<'home' | 'conversations'>('home');

  const handleSend = (textOverride?: string) => {
    const text = textOverride || inputValue.trim();
    if (!text) return;

    const newMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text,
      createdAt: Date.now(),
    };

    if (!activeId) {
      const tempId = crypto.randomUUID();
      const tempSourceId = crypto.randomUUID();

      const conv: Conversation = {
        id: tempId,
        title: 'Nova conversa',
        lastActivityAt: Date.now(),
        sourceId: tempSourceId,
        isTemp: true,
        status: 'open',
      };

      setConversations((prev) => [conv, ...prev]);
      setMessages((prev) => ({ ...prev, [tempId]: [newMsg] }));
      setActiveId(tempId);
      setActiveTab('chat');
      setTypingByConversation((prev) => ({ ...prev, [tempId]: true }));
      setViewMode('conversations');
      setInputValue('');

      if (readyState === ReadyState.OPEN) {
        sendJsonMessage({
          type: 'MESSAGE',
          userId: userId,
          name: userName,
          question: text,
          sourceId: tempSourceId,
          email: userEmail,
        });
      }
      return;
    }

    setMessages((prev) => ({
      ...prev,
      [activeId]: [...(prev[activeId] || []), newMsg],
    }));

    setTypingByConversation((prev) => ({ ...prev, [activeId]: true }));
    setInputValue('');

    const conv = conversations.find((c) => c.id === activeId);

    if (!conv) {
      const tempId = crypto.randomUUID();
      const tempSourceId = crypto.randomUUID();
      const newConv: Conversation = {
        id: tempId,
        title: 'Nova conversa',
        lastActivityAt: Date.now(),
        sourceId: tempSourceId,
        isTemp: true,
        status: 'open',
      };
      setConversations((prev) => [newConv, ...prev]);
      setMessages((prev) => ({
        ...prev,
        [tempId]: [newMsg],
      }));
      setActiveId(tempId);
      if (readyState === ReadyState.OPEN) {
        sendJsonMessage({
          type: 'MESSAGE',
          userId: userId,
          name: userName,
          question: text,
          sourceId: tempSourceId,
          email: userEmail,
        });
      }
      return;
    }

    if (readyState === ReadyState.OPEN) {
      sendJsonMessage({
        type: 'MESSAGE',
        userId: userId,
        name: userName,
        question: text,
        ...(conv.isTemp ? {} : { conversationId: conv.id }),
        sourceId: conv.sourceId,
        email: userEmail,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  if (!document || !document.body) return null;

  return ReactDOM.createPortal(
    <St.Container>
      <St.Overlay $isOpen={isChatOpen} onClick={closeChat} />
      <St.Drawer $isOpen={isChatOpen}>
        {viewMode === 'home' ? (
          <>
            <St.HomeHeader>
              <St.HomeHeaderTop>
                <St.UserGreeting>
                  <St.AgentAvatar>
                    <img src={teo} alt="Teo" />
                  </St.AgentAvatar>
                  <St.GreetingText>
                    Olá, {userName?.split(' ')[0] || 'Visitante'}!
                  </St.GreetingText>
                  <St.SubGreetingText>Como posso te ajudar?</St.SubGreetingText>
                </St.UserGreeting>
                <St.CloseButton $light onClick={closeChat}>
                  ×
                </St.CloseButton>
              </St.HomeHeaderTop>
            </St.HomeHeader>

            <St.Content style={{ backgroundColor: '#f9f9f9' }}>
              <St.HomeInputArea>
                <St.InputHome
                  placeholder="Envie uma mensagem..."
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: '0',
                  }}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleHomeInputEnter}
                />
                <St.SendButton
                  onClick={() => handleHomeInputEnter({ key: 'Enter' } as any)}
                  disabled={!inputValue.trim()}
                >
                  <VscSend size={24} color="#545454" />
                </St.SendButton>
              </St.HomeInputArea>

              <St.HomeContent>
                <St.SuggestionsTitle>
                  Sugestões de dúvidas para o Teo
                </St.SuggestionsTitle>
                <St.SuggestionList>
                  {suggestions.map((sug, idx) => (
                    <St.SuggestionCard
                      key={idx}
                      onClick={() => handleSuggestionClick(sug)}
                    >
                      {sug}
                    </St.SuggestionCard>
                  ))}
                </St.SuggestionList>
              </St.HomeContent>
            </St.Content>
          </>
        ) : (
          <>
            <St.HomeHeader>
              <St.ChatHeaderTop>
                <St.AgentAvatar>
                  <img src={teo} alt="Teo" />
                </St.AgentAvatar>

                <St.TitleNewChat>
                  <St.TitleText>
                    {conversations.find((c) => c.id === activeId)?.title || ''}
                  </St.TitleText>
                </St.TitleNewChat>
                <St.CloseButton $light onClick={closeChat}>
                  ×
                </St.CloseButton>
              </St.ChatHeaderTop>
              <St.TabContainer>
                <St.TabButton
                  $active={activeTab === 'chat'}
                  onClick={() => setActiveTab('chat')}
                >
                  Chat
                </St.TabButton>
                <St.TabButton
                  $active={activeTab === 'history'}
                  onClick={() => {
                    if (!conversations.length) setIsLoadingHistory(true);
                    loadConversations();
                    setActiveTab('history');
                  }}
                >
                  Conversas
                </St.TabButton>
              </St.TabContainer>
            </St.HomeHeader>

            <St.Content>
              {activeTab === 'chat' ? (
                <St.MessagesArea>
                  {currentMessages.map((msg) => (
                    <St.MessageBubble key={msg.id} $role={msg.role}>
                      {msg.text}
                      <St.MessageDate>
                        {formatDateHour(msg.createdAt)}
                      </St.MessageDate>
                    </St.MessageBubble>
                  ))}
                  {isLoadingChat && (
                    <St.CenterLoading>
                      <span></span>
                      <span></span>
                      <span></span>
                    </St.CenterLoading>
                  )}
                  {typingByConversation[activeId] && (
                    <St.TypingIndicator>
                      <span></span>
                      <span></span>
                      <span></span>
                    </St.TypingIndicator>
                  )}
                  <div ref={messagesEndRef} />
                </St.MessagesArea>
              ) : (
                <St.HistoryList>
                  <St.TabButton
                    $active={false}
                    onClick={() => {
                      handleNewChat();
                      setViewMode('conversations');
                    }}
                    style={{
                      marginBottom: 10,
                      background: '#000',
                      color: 'white',
                      justifyContent: 'center',
                      display: 'flex',
                      maxHeight: '35px',
                    }}
                  >
                    + Nova Conversa
                  </St.TabButton>
                  {isLoadingHistory ? (
                    <St.CenterLoading>
                      <span></span>
                      <span></span>
                      <span></span>
                    </St.CenterLoading>
                  ) : (
                    <>
                      {conversations.map((conv) => (
                        <St.HistoryItem
                          key={conv.id}
                          $active={conv.id === activeId}
                          onClick={() => handleHistorySelect(conv.id)}
                        >
                          <St.HistoryContent>
                            <St.HistoryTitle>{conv.title}</St.HistoryTitle>
                            <St.HistoryDate>
                              {formatDate(conv.lastActivityAt)}
                            </St.HistoryDate>
                          </St.HistoryContent>
                          <St.StatusBadge status={conv.status}>
                            {conv.status === 'open' || conv.status === 'pending'
                              ? 'Aberta'
                              : 'Encerrada'}
                          </St.StatusBadge>
                        </St.HistoryItem>
                      ))}
                    </>
                  )}
                </St.HistoryList>
              )}
            </St.Content>

            {activeTab === 'chat' && (
              <St.ChatInput>
                <St.InputArea>
                  <St.Input
                    placeholder="Digite sua mensagem..."
                    style={{
                      border: 'none',
                      background: 'transparent',
                      padding: '0',
                    }}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  <St.SendButton
                    onClick={() => handleSend()}
                    disabled={!inputValue.trim()}
                  >
                    <VscSend size={24} color="#545454" />
                  </St.SendButton>
                </St.InputArea>
              </St.ChatInput>
            )}
          </>
        )}

        <St.BottomTabBar>
          <St.BottomTabItem
            $active={viewMode === 'home'}
            onClick={() => setViewMode('home')}
          >
            <FiHome size={24} />
            <label>Início</label>
          </St.BottomTabItem>
          <St.BottomTabItem
            $active={viewMode === 'conversations'}
            onClick={() => {
              setViewMode('conversations');
              if (!activeId) {
                setIsLoadingHistory(true);
                loadConversations();
              }
              setActiveTab('history');
            }}
          >
            <FiMessageSquare size={24} />
            <label>Conversas</label>
          </St.BottomTabItem>
        </St.BottomTabBar>
      </St.Drawer>
    </St.Container>,
    document.body,
  );
};

export default ChatDrawer;
